import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { DataRoomFolder } from "@/types/dataRoom"
import { classNames } from "@/utils/classNames"
import {
  IconDots,
  IconFolder,
  IconFolderPlus,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react"
import * as m from "motion/react-m"
import { useState } from "react"

/* ------------------------------------------------------------------ */
/* Folder menu (shared by cards and the folder-view header)            */
/* ------------------------------------------------------------------ */

interface DataRoomFolderMenuProps {
  folder: DataRoomFolder
  hasContent: boolean
  onRequestCreate: (parentId: string | null) => void
  onRequestRename: (folder: DataRoomFolder) => void
  onRequestDelete: (folder: DataRoomFolder) => void
  className?: string
}

export const DataRoomFolderMenu: React.FC<DataRoomFolderMenuProps> = ({
  folder,
  hasContent,
  onRequestCreate,
  onRequestRename,
  onRequestDelete,
  className,
}) => {
  const { translate } = useTranslation()
  // Seed folders (turnus's onboarding taxonomy) can never be renamed or
  // deleted by a customer; the backend enforces the same rule.
  const canManage = !folder.isTurnusCreated
  const isDeleteDisabled = hasContent

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={classNames(
            "h-8 w-8 text-grey5 hover:text-white data-[state=open]:text-white",
            className,
          )}
          aria-label={translate("DATA_ROOM/FOLDER_ACTIONS_ARIA", {
            name: folder.name,
          })}
          onClick={(e) => e.stopPropagation()}
        >
          <IconDots size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onRequestCreate(folder.id)}>
          <IconFolderPlus size={14} />
          {translate("DATA_ROOM/FOLDER_ADD_SUBFOLDER")}
        </DropdownMenuItem>
        {canManage && (
          <DropdownMenuItem onClick={() => onRequestRename(folder)}>
            <IconPencil size={14} />
            {translate("DATA_ROOM/FOLDER_RENAME")}
          </DropdownMenuItem>
        )}
        {canManage && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <DropdownMenuItem
                    onClick={() => onRequestDelete(folder)}
                    disabled={isDeleteDisabled}
                    className="text-red1 focus:text-red1"
                  >
                    <IconTrash size={14} />
                    {translate("DATA_ROOM/FOLDER_DELETE")}
                  </DropdownMenuItem>
                </span>
              </TooltipTrigger>
              {isDeleteDisabled && (
                <TooltipContent side="right">
                  {translate("DATA_ROOM/FOLDER_DELETE_DISABLED_REASON")}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/* ------------------------------------------------------------------ */
/* Folder cards                                                        */
/* ------------------------------------------------------------------ */

interface DataRoomFolderGridProps {
  folders: DataRoomFolder[]
  /** The parent these folders belong to; new folders are created here. */
  parentId: string | null
  fileCountByFolderId: Map<string, number>
  hasContent: (folderId: string) => boolean
  onSelect: (folderId: string) => void
  onRequestCreate: (parentId: string | null) => void
  onRequestRename: (folder: DataRoomFolder) => void
  onRequestDelete: (folder: DataRoomFolder) => void
  /** `compact` is the smaller card used for subfolders inside a folder. */
  size?: "default" | "compact"
}

/** Quiet folder cards: a surface, a name, what belongs inside. Each card is
 * one button; its "..." menu is a sibling that only appears on hover. */
export const DataRoomFolderGrid: React.FC<DataRoomFolderGridProps> = ({
  folders,
  parentId,
  fileCountByFolderId,
  hasContent,
  onSelect,
  onRequestCreate,
  onRequestRename,
  onRequestDelete,
  size = "default",
}) => {
  const { translate } = useTranslation()
  const compact = size === "compact"

  return (
    <div className="flex flex-col gap-3">
      <ul
        role="list"
        aria-label={translate("DATA_ROOM/FOLDER_GRID_ARIA")}
        data-testid="data-room-folder-grid"
        className={classNames(
          "grid gap-3",
          compact
            ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
            : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4",
        )}
      >
        {folders.map((folder, index) => {
          const count = fileCountByFolderId.get(folder.id) ?? 0
          return (
            <m.li
              key={folder.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.2,
                ease: "easeOut",
                delay: Math.min(index, 8) * 0.03,
              }}
              className="group relative"
            >
              <button
                type="button"
                onClick={() => onSelect(folder.id)}
                className={classNames(
                  "flex h-full w-full flex-col rounded-xl bg-grey2 text-left transition-colors duration-150 ease-out",
                  "hover:bg-grey4/40 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                  compact ? "min-h-[104px] p-4" : "min-h-[150px] p-5",
                )}
              >
                <IconFolder
                  size={compact ? 20 : 24}
                  className="text-white"
                  aria-hidden="true"
                />
                <Typography
                  variant={compact ? "textS-bold" : "textM-bold"}
                  className="mt-4 text-balance wrap-break-word"
                >
                  {folder.name}
                </Typography>
                {folder.description && (
                  <Typography
                    variant="textXS"
                    className="mt-1 line-clamp-2 text-grey5"
                  >
                    {folder.description}
                  </Typography>
                )}
                {count > 0 && (
                  <Typography
                    variant="textXS"
                    className="mt-auto pt-4 text-grey5"
                  >
                    {translate("DATA_ROOM/FILE_COUNT", { count })}
                  </Typography>
                )}
              </button>

              <div
                className={classNames(
                  "absolute right-2 top-2 transition-opacity duration-150",
                  "opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 has-data-[state=open]:opacity-100",
                )}
              >
                <DataRoomFolderMenu
                  folder={folder}
                  hasContent={hasContent(folder.id)}
                  onRequestCreate={onRequestCreate}
                  onRequestRename={onRequestRename}
                  onRequestDelete={onRequestDelete}
                />
              </div>
            </m.li>
          )
        })}
      </ul>

      <div>
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 gap-1.5 text-grey5 hover:text-white"
          onClick={() => onRequestCreate(parentId)}
          data-testid="data-room-new-folder"
          aria-label={translate("DATA_ROOM/FOLDER_CREATE_ARIA")}
        >
          <IconPlus size={14} aria-hidden="true" />
          {translate("DATA_ROOM/NEW_FOLDER_CARD")}
        </Button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Folder dialogs (create / rename / delete), owned by the page         */
/* ------------------------------------------------------------------ */

export type FolderDialogState =
  | { mode: "create"; parentId: string | null }
  | { mode: "rename"; folder: DataRoomFolder }
  | { mode: "delete"; folder: DataRoomFolder }

interface DataRoomFolderDialogsProps {
  state: FolderDialogState | null
  onClose: () => void
  /** Resolve on success, reject on failure. The dialog stays open with the
   * draft intact until the operation actually succeeded. */
  onCreate: (name: string, parentId: string | null) => Promise<unknown>
  onRename: (folderId: string, name: string) => Promise<unknown>
  onDelete: (folderId: string) => Promise<unknown>
  /** Maps a failed operation's error to the copy shown beside the field. */
  errorLabel: (error: unknown) => string
}

export const DataRoomFolderDialogs: React.FC<DataRoomFolderDialogsProps> = ({
  state,
  onClose,
  onCreate,
  onRename,
  onDelete,
  errorLabel,
}) => {
  const { translate } = useTranslation()
  const [nameDraft, setNameDraft] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The draft resets whenever a name dialog opens: a rename shows the
  // current name, a create starts blank.
  const [seededFor, setSeededFor] = useState<FolderDialogState | null>(null)
  if (state !== seededFor) {
    setSeededFor(state)
    setNameDraft(state?.mode === "rename" ? state.folder.name : "")
    setError(null)
    setIsSubmitting(false)
  }

  const isNameDialog = state?.mode === "create" || state?.mode === "rename"

  const run = async (operation: () => Promise<unknown>) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setError(null)
    try {
      await operation()
      onClose()
    } catch (failure) {
      setError(errorLabel(failure))
    } finally {
      setIsSubmitting(false)
    }
  }

  const submitName = () => {
    const trimmed = nameDraft.trim()
    if (!trimmed || !state) return
    if (state.mode === "create") {
      void run(() => onCreate(trimmed, state.parentId))
    }
    if (state.mode === "rename") {
      void run(() => onRename(state.folder.id, trimmed))
    }
  }

  return (
    <>
      <Dialog open={isNameDialog} onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {state?.mode === "rename"
                ? translate("DATA_ROOM/FOLDER_RENAME_TITLE")
                : translate("DATA_ROOM/FOLDER_CREATE_TITLE")}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="data-room-folder-name">
              {translate("DATA_ROOM/FOLDER_NAME_LABEL")}
            </Label>
            <Input
              id="data-room-folder-name"
              value={nameDraft}
              onChange={(e) => {
                setNameDraft(e.target.value)
                setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitName()
              }}
              maxLength={255}
              autoFocus
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? "data-room-folder-error" : undefined}
            />
            {error && (
              <div
                role="alert"
                id="data-room-folder-error"
                data-testid="data-room-folder-error"
              >
                <Typography variant="textXS" className="text-red1">
                  {error}
                </Typography>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              {translate("DATA_ROOM/FOLDER_DIALOG_CANCEL")}
            </Button>
            <Button
              onClick={submitName}
              disabled={!nameDraft.trim() || isSubmitting}
            >
              {translate("DATA_ROOM/FOLDER_DIALOG_SAVE")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={state?.mode === "delete"}
        onOpenChange={(open) => !open && onClose()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate("DATA_ROOM/FOLDER_DELETE_TITLE")}
            </DialogTitle>
          </DialogHeader>
          <Typography variant="textS" className="text-grey5">
            {translate("DATA_ROOM/FOLDER_DELETE_DESCRIPTION")}
          </Typography>
          {error && (
            <div role="alert" data-testid="data-room-folder-error">
              <Typography variant="textXS" className="text-red1">
                {error}
              </Typography>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              {translate("DATA_ROOM/FOLDER_DIALOG_CANCEL")}
            </Button>
            <Button
              variant="destructive"
              disabled={isSubmitting}
              onClick={() => {
                if (state?.mode === "delete") {
                  void run(() => onDelete(state.folder.id))
                }
              }}
            >
              {translate("DATA_ROOM/FOLDER_DELETE")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
